"use client";
import { RegistrationAccountPreview } from "./RegistrationAccountPreview";
import Link from "next/link";
import { useState } from "react";
import { RegistrationWelcome, MerchantChoices } from "./RegistrationChoices";
import { agentSteps, creatorSteps, merchantSteps, OnboardingSteps } from "./OnboardingSteps";
import { OnboardingHeading } from "./OnboardingHeading";
import { MiniappLogin } from "@/features/login/components/MiniappLogin";

import type { AgentType } from "../agent-type";
import type { SuppliedConsentChoices } from "../supplied-documents";

type Kind = "creator" | "director" | "brand" | "agent";
export function RegistrationFlow({ initialKind, smsEnabled, error, previewOnly, localPreview, signedInHome }: { initialKind?: string; signedInHome?: string; smsEnabled: boolean; error?: string; previewOnly: boolean; localPreview?: { agentType?: AgentType | null; city?: string; index?: number; onSensitiveSigned?: (choices: SuppliedConsentChoices) => void; onRegistered?: (kind: Kind, city: string | null, agentType?: AgentType | null) => void; onSelect: (index: number) => void } }) {
  const [leaving, setLeaving] = useState(false);
  const [sessionError, setSessionError] = useState("");
  async function startNewRegistration() {
    if (leaving) return;
    setLeaving(true);
    setSessionError("");
    try {
      const response = await fetch("/api/auth/logout", { method: "POST" });
      if (!response.ok) throw new Error("退出未完成，请重试");
      const query = new URLSearchParams();
      if (initialKind) query.set("kind", initialKind);
      window.location.replace(query.size ? `/register?${query}` : "/register");
    } catch {
      setSessionError("退出未完成，请重试。");
      setLeaving(false);
    }
  }
  const [kind, setKind] = useState<Kind | null>(initialKind === "creator" || initialKind === "director" || initialKind === "brand" || initialKind === "agent" ? initialKind : null);
  const agent = initialKind === "agent";
  const agentType = "mcn" as const;
  const agentCity = null;
  const merchant = initialKind === "merchant" || initialKind === "director" || initialKind === "brand";
  const [chosen, setChosen] = useState(localPreview ? initialKind === "creator" || localPreview.index === 1 : initialKind === "creator" || initialKind === "director" || initialKind === "brand");
  if (!initialKind) return <RegistrationWelcome />;
  return <div className="rg-wrap">
    <OnboardingHeading merchant={merchant || agent} agent={agent} />
    <OnboardingSteps labels={agent ? agentSteps : merchant ? merchantSteps : creatorSteps} current={agent || (merchant && chosen) ? 1 : 0} onSelect={localPreview?.onSelect} />
    <section className="rg-card">
      {merchant && !chosen ? <>
        <MerchantChoices value={kind} onChange={selected => {
          setKind(selected);
          if (localPreview) { localPreview.onRegistered?.(selected, null); localPreview.onSelect(1); }
          else setChosen(true);
        }} />
      </> : <>
        <h2 className="sr-only">注册账号</h2>
        {previewOnly && !localPreview && <p role="status" className="rg-notice rg-error">注册服务更新中，请稍后重试。已有账号可直接登录。</p>}
        {error && <p role="alert" className="rg-error">{error}</p>}
        {signedInHome && !localPreview ? <div className="rg-form">
          <p className="rg-notice">当前浏览器已有账号登录。注册新账号请先退出；也可以继续当前账号的流程。</p>
          {sessionError && <p role="alert" className="rg-error">{sessionError}</p>}
          <button type="button" disabled={leaving} className="rg-primary" onClick={() => void startNewRegistration()}>{leaving ? "正在退出…" : "退出当前账号并注册"}</button>
          <Link href={signedInHome} className="rg-login-link">{signedInHome === "/onboarding" ? "继续当前账号入驻" : "返回当前账号控制台"}</Link>
        </div> : localPreview ? <RegistrationAccountPreview creator={kind === "creator"} onSensitiveSigned={localPreview.onSensitiveSigned} onContinue={() => { if (kind) localPreview.onRegistered?.(kind, agentCity, agentType); localPreview.onSelect(merchant || agent ? 2 : 1); }} /> : smsEnabled ? <fieldset disabled={previewOnly} className="min-w-0 disabled:opacity-60">
          <MiniappLogin key={kind} challengeEnabled={!localPreview} smsEnabled initialMode="sms" next="/onboarding" registration={{ role: merchant || agent ? "partner" : "creator", kind: kind!, ...(agent ? { agentType } : {}) }} />
        </fieldset> : <p role="status" className="rg-notice rg-error">手机号注册暂不可用，请稍后重试。</p>}
        {merchant && <div className="rg-actions rg-back"><button type="button" className="rg-secondary" onClick={() => localPreview ? localPreview.onSelect(0) : setChosen(false)}>上一步</button></div>}
      </>}
    </section>
  </div>;
}
